import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { appointmentService } from '../services/api';
import { Appointment } from '../types';
import { 
  formatDate, 
  formatTime, 
  getStatusColor, 
  getStatusLabel, 
  getEmailStatusColor, 
  getEmailStatusLabel, 
  getSMSStatusColor, 
  getSMSStatusLabel 
} from '../utils/statusUtils';
import './AppointmentDetails.css';

const AppointmentDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [appointment, setAppointment] = useState<Appointment | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [statusUpdateLoading, setStatusUpdateLoading] = useState<boolean>(false);

  useEffect(() => {
    const fetchAppointment = async () => {
      if (!id) return;
      
      try {
        const data = await appointmentService.getById(parseInt(id));
        setAppointment(data);
        setLoading(false);
      } catch (err) {
        setError('Erreur lors du chargement du rendez-vous');
        setLoading(false);
        console.error(err);
      }
    };

    fetchAppointment();
  }, [id]);

  const handleStatusUpdate = async (status: Appointment['status']) => {
    if (!id || !appointment) return;
    
    setStatusUpdateLoading(true);
    try {
      const updatedAppointment = await appointmentService.updateStatus(parseInt(id), status);
      setAppointment(updatedAppointment);
      setStatusUpdateLoading(false);
    } catch (err) {
      setError('Erreur lors de la mise à jour du statut');
      setStatusUpdateLoading(false);
      console.error(err);
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce rendez-vous ?')) {
      try {
        await appointmentService.delete(parseInt(id));
        navigate('/appointments');
      } catch (err) {
        setError('Erreur lors de la suppression du rendez-vous');
        console.error(err);
      }
    }
  };

  if (loading) {
    return <div className="loading">Chargement...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  if (!appointment) {
    return <div className="error">Rendez-vous non trouvé</div>;
  }

  return (
    <div className="appointment-details-page">
      <div className="appointment-details-header">
        <h1>Détails du rendez-vous</h1>
        <div className="header-actions">
          <Link to={`/appointments/${id}/edit`} className="btn btn-primary">
            Modifier
          </Link>
          <button onClick={handleDelete} className="btn btn-danger">
            Supprimer
          </button>
        </div>
      </div>

      <div className="appointment-details-card">
        <div className="appointment-details-section">
          <h2>Informations du rendez-vous</h2>
          <div className="details-grid">
            <div className="detail-item">
              <span className="detail-label">Type</span>
              <span className="detail-value">{appointment.type}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Date</span>
              <span className="detail-value">{formatDate(appointment.date)}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Heure</span>
              <span className="detail-value">{formatTime(appointment.time)}</span>
            </div>
          </div>
        </div>

        <div className="appointment-details-section">
          <h2>Informations du patient</h2>
          <div className="details-grid">
            <div className="detail-item">
              <span className="detail-label">Nom complet</span>
              <span className="detail-value">
                {appointment.gender === 'M' ? 'M.' : 'Mme'} {appointment.name} {appointment.surname}
              </span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Email</span>
              <span className="detail-value">{appointment.email}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Téléphone</span>
              <span className="detail-value">{appointment.phone}</span>
            </div>
          </div>
        </div>

        <div className="appointment-details-section">
          <h2>Statuts des communications</h2>
          <div className="communication-status-grid">
            <div className="communication-status-item">
              <h3>Email</h3>
              <span className={`status-badge ${getEmailStatusColor(appointment.email_status)}`}>
                {getEmailStatusLabel(appointment.email_status)}
              </span>
              {appointment.email_details && (
                <div className="status-details">
                  <p>Détails: {
                    typeof appointment.email_details === 'string' 
                      ? appointment.email_details 
                      : JSON.stringify(appointment.email_details, null, 2)
                  }</p>
                </div>
              )}
            </div>
            <div className="communication-status-item">
              <h3>SMS</h3>
              <span className={`status-badge ${getSMSStatusColor(appointment.sms_status)}`}>
                {getSMSStatusLabel(appointment.sms_status)}
              </span>
              {appointment.sms_details && (
                <div className="status-details">
                  <p>Détails: {
                    typeof appointment.sms_details === 'string' 
                      ? appointment.sms_details 
                      : JSON.stringify(appointment.sms_details, null, 2)
                  }</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="back-link">
        <Link to="/appointments" className="btn btn-link">
          &larr; Retour à la liste des rendez-vous
        </Link>
      </div>
    </div>
  );
};

export default AppointmentDetails;
